import { parseAttendeeList } from "@xop/dashboard-shared";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useRef, useState, type JSX } from "react";

import { useCan } from "../../../../auth/permissions.js";
import { toast } from "../../../../components/ui/use-toast.js";
import { webinarsApi } from "../client.js";
import {
  CampaignStatus,
  DoneNote,
  OpenCampaignButton,
  SafetyNote,
  StepFooter,
  StepIntro,
  type StepProps,
} from "../stepShared.js";

/**
 * Step 2 — who joined, then thank them.
 *
 * Every webinar platform exports a different file and the operator should not
 * have to care, so this takes ANY paste: a Zoom/Teams CSV, a spreadsheet
 * paste, or just a column of addresses. It shows what it understood BEFORE
 * anything is saved ("12 people found, 1 line we could not read"), because a
 * wrong paste discovered after the fact is how a thank-you goes to the wrong
 * list.
 *
 * The preview runs the SAME parser the server does (`parseAttendeeList` in
 * packages/shared), so the count on screen can never disagree with the import.
 */
export function StepAttendees({ detail, refresh, canEdit, markDone }: StepProps): JSX.Element {
  const { webinar, attendees, campaigns } = detail;
  const canReachCampaigns = useCan("crm:view");
  const [text, setText] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  // Live preview as they type/paste — same parser, so no surprises on import.
  const preview = useMemo(() => (text.trim() ? parseAttendeeList(text) : null), [text]);

  const importRows = useMutation({
    mutationFn: () => webinarsApi.importAttendees(webinar.id, text),
    onSuccess: (res) => {
      setText("");
      refresh();
      toast({
        title: `${res.added} added, ${res.updated} updated`,
        ...(res.skipped.length
          ? { description: `${res.skipped.length} line(s) had no email address and were skipped.` }
          : {}),
      });
    },
    onError: (e: Error) =>
      toast({ title: "Could not save the list", description: e.message, variant: "destructive" }),
  });

  const removeOne = useMutation({
    mutationFn: (attendeeId: number) => webinarsApi.removeAttendee(webinar.id, attendeeId),
    onSuccess: () => refresh(),
  });

  const thankThem = useMutation({
    mutationFn: () => webinarsApi.createThankYouCampaign(webinar.id),
    onSuccess: (res) => {
      refresh();
      toast({
        title: "Thank-you email drafted",
        description: `Addressed to the ${res.recipients} people who joined. It is a draft - open it and press Send.`,
      });
    },
    onError: (e: Error) =>
      toast({ title: "Could not draft the email", description: e.message, variant: "destructive" }),
  });

  const joined = attendees.filter((a) => a.joined);
  const thankYouId = webinar.thankyou_campaign_id;

  const readFile = (file: File): void => {
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result ?? ""));
    reader.onerror = () =>
      toast({ title: "Could not read that file", variant: "destructive" });
    reader.readAsText(file);
  };

  return (
    <Box>
      <StepIntro>
        Paste the list of people who joined - straight out of Zoom, Teams, or a spreadsheet. We
        work out the names and email addresses for you.
      </StepIntro>

      {canEdit && (
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <TextField
            label="Paste the attendee list here"
            placeholder={"Name, Email, Company\nJane Doe, jane@acme.com, Acme"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline
            minRows={5}
            fullWidth
            inputProps={{ "data-testid": "attendee-paste" }}
          />
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={() => importRows.mutate()}
              disabled={!preview || preview.rows.length === 0 || importRows.isPending}
              data-testid="save-attendees"
            >
              {importRows.isPending
                ? "Saving…"
                : preview
                  ? `Save these ${preview.rows.length} people`
                  : "Save the list"}
            </Button>
            <Button
              variant="text"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInput.current?.click()}
            >
              Or pick a CSV file
            </Button>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/plain"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) readFile(file);
                e.target.value = "";
              }}
              data-testid="attendee-file"
            />
          </Box>

          {preview && (
            <Alert severity={preview.rows.length ? "success" : "warning"} data-testid="import-preview">
              We found <strong>{preview.rows.length}</strong>{" "}
              {preview.rows.length === 1 ? "person" : "people"}
              {preview.skipped.length > 0 && (
                <>
                  {" "}
                  and could not read <strong>{preview.skipped.length}</strong>{" "}
                  {preview.skipped.length === 1 ? "line" : "lines"} (no email address):{" "}
                  <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                    {preview.skipped.slice(0, 3).join(" · ")}
                    {preview.skipped.length > 3 ? " …" : ""}
                  </Box>
                </>
              )}
              . Nothing is saved until you press the button.
            </Alert>
          )}
        </Stack>
      )}

      {attendees.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
            {attendees.length} on the list · {joined.length} actually joined
          </Typography>
          <Box sx={{ maxHeight: 320, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Joined</TableCell>
                  {canEdit && <TableCell align="right" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {attendees.map((a) => (
                  <TableRow key={a.id} data-testid={`attendee-row-${a.id}`}>
                    <TableCell>{a.name || "-"}</TableCell>
                    <TableCell sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                      {a.email}
                    </TableCell>
                    <TableCell>{a.company || "-"}</TableCell>
                    <TableCell>
                      {a.joined ? (
                        <Chip
                          size="small"
                          color="success"
                          label={a.minutes_attended ? `${a.minutes_attended} min` : "Yes"}
                        />
                      ) : (
                        <Chip size="small" variant="outlined" label="No" />
                      )}
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label={`Remove ${a.email}`}
                          onClick={() => removeOne.mutate(a.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {canReachCampaigns && (
        <Stack spacing={2}>
          {thankYouId ? (
            <>
              <DoneNote>The thank-you email exists.</DoneNote>
              <CampaignStatus summary={campaigns.thankyou} />
              <Box>
                <OpenCampaignButton campaignId={thankYouId} label="Open the thank-you email" />
              </Box>
            </>
          ) : (
            <Box>
              <Button
                variant="contained"
                size="large"
                onClick={() => thankThem.mutate()}
                disabled={!canEdit || joined.length === 0 || thankThem.isPending}
                data-testid="draft-thankyou"
              >
                {thankThem.isPending
                  ? "Writing…"
                  : `Write a thank-you to the ${joined.length} who joined`}
              </Button>
            </Box>
          )}
          <SafetyNote />
        </Stack>
      )}

      <StepFooter
        step="attendees"
        detail={detail}
        markDone={markDone}
        canEdit={canEdit}
        {...(joined.length === 0
          ? { disabledReason: "Add the people who joined first." }
          : {})}
      />
    </Box>
  );
}
